import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';

const Shop = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    {
      id: 'membership-upgrade',
      name: 'Membership Upgrade',
      description: 'Upgrade your membership to access exclusive benefits',
      icon: 'pi pi-star',
      color: 'primary',
      itemCount: 3
    },
    {
      id: 'clothing-merchandise',
      name: 'Clothing & Merchandise',
      description: 'Official Sport Endurance branded clothing and accessories',
      icon: 'pi pi-shopping-bag',
      color: 'secondary',
      itemCount: 8
    },
    {
      id: 'gift-tokens',
      name: 'Gift Tokens',
      description: 'Gift vouchers for rides, events, and merchandise',
      icon: 'pi pi-gift',
      color: 'tertiary',
      itemCount: 4
    },
    {
      id: 'hivis-clothing',
      name: 'HiVis Clothing',
      description: 'High visibility safety clothing for rides',
      icon: 'pi pi-shield',
      color: 'warning',
      itemCount: 5
    },
    {
      id: 'trailer-car-stickers',
      name: 'Trailer & Car Stickers',
      description: 'Show your Sport Endurance pride on the road',
      icon: 'pi pi-car',
      color: 'info',
      itemCount: 6
    }
  ];

  const featuredProducts = [
    {
      id: 'henbury-polo',
      name: 'Henbury Ladies Polo HB401 with logo',
      price: 22.50,
      image: '/api/placeholder/200/200',
      description: 'Women\'s 65/35 polo. Stand up collar for increased shape retention.',
      category: 'clothing-merchandise',
      inStock: true
    },
    {
      id: 'gift-voucher-rides',
      name: 'Gift voucher for on-line orders',
      price: 25.00,
      image: '/api/placeholder/200/200',
      description: 'Credit Vouchers to give to your friends!',
      category: 'gift-tokens',
      inStock: true
    },
    {
      id: 'unisex-hoodie',
      name: 'Unisex hoodie JH001 with logo',
      price: 35.00,
      image: '/api/placeholder/200/200',
      description: 'Twin needle stitching. Double fabric hood with cords.',
      category: 'clothing-merchandise',
      inStock: true
    },
    {
      id: 'baseball-cap',
      name: 'Baseball Cap Ultimate 5 panel',
      price: 18.00,
      image: '/api/placeholder/200/200',
      description: 'Contemporary styling. Rip-Strip size adjuster.',
      category: 'clothing-merchandise',
      inStock: true
    }
  ];

  const filteredProducts = featuredProducts.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getBadgeColor = (color) => {
    const colorMap = {
      primary: 'primary',
      secondary: 'info',
      tertiary: 'danger',
      warning: 'warning',
      info: 'success'
    };
    return colorMap[color] || 'primary';
  };

  return (
    <div>
      {/* Header */}
      <div className="se-card mb-4">
        <div className="se-card-header">
          <h1>Shop</h1>
        </div>
        <div className="se-card-content">
          <p className="text-600 mb-3">
            Browse our range of official Sport Endurance merchandise, gift vouchers, and membership upgrades.
            All clothing prices include SE logo and postage.
          </p>
          
          {/* Search */}
          <div className="flex gap-2">
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="flex-1"
            />
            <Button
              icon="pi pi-search"
              className="p-button-outlined"
              onClick={() => {}}
            />
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid mb-4">
        <div className="col-12">
          <h2 className="se-text-primary mb-3">Shop Categories</h2>
        </div>
        {categories.map((category) => (
          <div key={category.id} className="col-12 md:col-6 lg:col-4">
            <Card className="h-full cursor-pointer hover:shadow-3">
              <div 
                className="text-center"
                onClick={() => navigate(`/shop/category/${category.id}`)}
              >
                <div className={`inline-flex align-items-center justify-content-center border-circle w-4rem h-4rem mb-3 se-bg-${category.color}`}>
                  <i className={`${category.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="mt-0 mb-2">{category.name}</h3>
                <p className="text-600 line-height-3 mb-3">{category.description}</p>
                <div className="flex justify-content-between align-items-center">
                  <Badge 
                    value={`${category.itemCount} items`} 
                    severity={getBadgeColor(category.color)}
                  />
                  <Button 
                    label="Browse" 
                    icon="pi pi-arrow-right"
                    className={`p-button-${category.color} p-button-sm`}
                  />
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>

      {/* Featured Products */}
      <div className="grid">
        <div className="col-12">
          <h2 className="se-text-primary mb-3">Featured Products</h2>
        </div>
        {filteredProducts.map((product) => (
          <div key={product.id} className="col-12 md:col-6 lg:col-3">
            <Card className="h-full">
              <div 
                className="cursor-pointer"
                onClick={() => navigate(`/shop/product/${product.id}`)}
              >
                <div className="text-center mb-3">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full border-round"
                    style={{ height: '200px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjhGOUZBIi8+CjxwYXRoIGQ9Ik0xMDAgNzBMMTMwIDEwMEg3MEwxMDAgNzBaIiBmaWxsPSIjREVFMkU2Ii8+CjxjaXJjbGUgY3g9IjEwMCIgY3k9IjEwMCIgcj0iNDAiIHN0cm9rZT0iI0RFRTJFNiIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTQwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNjY3MDdCIiBmb250LXNpemU9IjEyIj5Qcm9kdWN0IEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                    }}
                  />
                </div>
                
                <h4 className="mt-0 mb-2 line-height-3">{product.name}</h4>
                <p className="text-600 mb-3 text-sm">{product.description}</p>
                
                <div className="flex justify-content-between align-items-center mb-3">
                  <span className="font-bold text-xl se-text-primary">£{product.price.toFixed(2)}</span>
                  {product.inStock ? (
                    <Badge value="In Stock" severity="success" />
                  ) : (
                    <Badge value="Out of Stock" severity="danger" />
                  )}
                </div>

                <Button
                  label="View Details"
                  icon="pi pi-eye"
                  className="w-full p-button-outlined"
                  disabled={!product.inStock}
                />
              </div>
            </Card>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && searchTerm && (
        <div className="text-center py-4">
          <h3>No products found</h3>
          <p className="text-600">Try adjusting your search terms</p>
          <Button
            label="Clear Search"
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setSearchTerm('')}
          />
        </div>
      )}
    </div>
  );
};

export default Shop;